import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../ui/select';
import { Badge } from '../../ui/badge';
import { CheckCircle2, Circle, Lightbulb, PartyPopper } from 'lucide-react';
import { SCENARIOS, getScenarioProgress } from '../../../lib/uds/scenarios';

const ScenarioGuide = ({ activeScenarioId, setActiveScenarioId, log, ecuState }) => {
  const [showHint, setShowHint] = useState(false);
  const scenario = SCENARIOS.find((s) => s.id === activeScenarioId);
  const progress = scenario ? getScenarioProgress(scenario, log, ecuState) : null;

  return (
    <Card className="technical-card" data-testid="uds-scenario-guide">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-base">Guided Walkthrough</CardTitle>
        <CardDescription>Pick a scenario and follow the steps — each one checks itself off as you complete it.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Select value={activeScenarioId || ''} onValueChange={(v) => { setActiveScenarioId(v); setShowHint(false); }}>
          <SelectTrigger data-testid="uds-scenario-select"><SelectValue placeholder="Choose a scenario…" /></SelectTrigger>
          <SelectContent>
            {SCENARIOS.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
          </SelectContent>
        </Select>

        {scenario && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{scenario.description}</p>
            <ol className="space-y-2">
              {scenario.steps.map((step, i) => {
                const done = i < progress.currentStep;
                const current = i === progress.currentStep;
                return (
                  <li key={i} className={`flex items-start gap-2 text-sm rounded-md p-2 ${current ? 'bg-autoxium-blue/10 border border-autoxium-blue/30' : ''}`}>
                    {done ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" /> : <Circle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />}
                    <span className={done ? 'text-muted-foreground line-through' : ''}>{step.instruction}</span>
                  </li>
                );
              })}
            </ol>

            {progress.complete ? (
              <div className="flex items-start gap-2 text-sm bg-green-600/10 border border-green-600/30 rounded-md p-3" data-testid="uds-scenario-complete">
                <PartyPopper className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <Badge className="mb-1 bg-green-600 hover:bg-green-600/90">Scenario Complete</Badge>
                  <p>{scenario.successMessage}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Button variant="outline" size="sm" onClick={() => setShowHint((v) => !v)} data-testid="uds-hint-btn">
                  <Lightbulb className="h-3.5 w-3.5" /> {showHint ? 'Hide Hint' : 'Hint'}
                </Button>
                {showHint && (
                  <p className="text-sm bg-muted/50 rounded-md p-3" data-testid="uds-hint-text">{scenario.steps[progress.currentStep]?.hint}</p>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ScenarioGuide;
